//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react"
import {PanelData, PanelDirection, PanelType} from "./PanelData";

//#endregion

const ContextLayout = createContext();

//-----------------------------------------------------------------
export function LayoutProvider( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { name, children } = props;

  const ref_auto_id = useRef(10);

  const _layout = useMemo( () => loadLayout(name) || createDefaultLayout(), [name] )
  const [ layout ] = useState( _layout );
  const [ force_render, setForceRender ] = useState(0);

  const values = {
    root,
    getParent,
    getItem,
    moveItem,
    openItem,
    closeItem,
    createItem,
    removeItem,
    getSizes,
    setSizes,
    save,
    changeDirection,
    setType,
  };

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    const listener = (event) => {
      save();
    }

    window.addEventListener('beforeunload', listener );

    return () => {
      save();
      window.removeEventListener('beforeunload', listener );
    }
  }, [layout] )

  //#endregion

  //#region [METHOD] === EXTERNAL

  //-----------------------------------------------------------------
  function root() {
    //-----------------------------------------------------------------
    return layout.get(0);
  }

  //-----------------------------------------------------------------
  function getItem(id) {
    //-----------------------------------------------------------------
    return layout.get( id );
  }

  //-----------------------------------------------------------------
  function moveItem( a, b, is_above ) {
  //-----------------------------------------------------------------
    const parent_a = getParent(a.id);
    const parent_b = getParent(b.id);

    const index_a = parent_a.items.indexOf( a.id );
    const index_b = parent_b.items.indexOf( b.id );

    const   to_above = is_above;
    const from_above = index_a < index_b;
    const to_above_from_above =  to_above &&  from_above;
    const to_above_from_below =  to_above && !from_above;
    const to_below_from_above = !to_above &&  from_above;
    const to_below_from_below = !to_above && !from_above;

    const direction = to_above_from_above ? 0 : to_above_from_below ? 0 : to_below_from_above ? 1 : to_below_from_below ? 1 : 0;

    const is_same_parent = parent_a.id === parent_b.id;
    if( is_same_parent ) {
      parent_a.removeItem( index_a );
      const index_b_new = from_above ? index_b-1 : index_b;
      parent_b.createItem( index_b_new + direction, a.id );
    } else {
      parent_a.removeItem( index_a );
      parent_b.createItem( index_b + direction, a.id );
    }
    setForceRender( force_render+1 );
  }

  //-----------------------------------------------------------------
  function openItem( id ) {
    //-----------------------------------------------------------------
    layout.get( id )?.open();
    setForceRender( force_render+1 );
  }

  //-----------------------------------------------------------------
  function closeItem( id ) {
    //-----------------------------------------------------------------
    layout.get( id )?.close();
    setForceRender( force_render+1 );
  }

  //-----------------------------------------------------------------
  function createItem( parent_id ) {
    //-----------------------------------------------------------------
    const new_panel = createDefaultPanel();
    layout.set( new_panel.id, new_panel );
    const parent = getItem( parent_id );
    parent.items.push( new_panel.id );
  }

  //-----------------------------------------------------------------
  function removeItem( id ) {
    //-----------------------------------------------------------------
    layout.delete( id );
    const parent = getParent( id );
    const index = parent.items.indexOf( id );
    parent.items.splice( index, 1 );
  }

  //-----------------------------------------------------------------
  function changeDirection( id ) {
    //-----------------------------------------------------------------
    const item = getItem( id );
    item.changeDirection();
  }

  //-----------------------------------------------------------------
  function getSizes(id) {
    //-----------------------------------------------------------------
    const item = getItem(id);
    const temp = item.items.map( (c_id) => {
      const child = getItem(c_id);
      const value = child.is_open ? child.size : 0;
      return value;
    } )
           if( item.direction === PanelDirection.ROW ) {
      return { cols: [100] , rows: temp  };
    } else if( item.direction === PanelDirection.COL ) {
      return { cols: temp  , rows: [100] };
    }
  }
  
  //-----------------------------------------------------------------
  function setSizes( id, sizes ) {
    //-----------------------------------------------------------------
    const item = getItem(id);
    const values = sizes.cols.length>1 ? sizes.cols : sizes.rows;
    item.items.forEach( (c_id,index) => {
      const child = getItem(c_id);
      if( child.is_open ) child.setSize( values[index] );
    } );
    setForceRender( force_render+1 );
  }

  //-----------------------------------------------------------------
  function setType( id, type ) {
    //-----------------------------------------------------------------
    const item = getItem( id );
    item.setType( type );
  }

  //-----------------------------------------------------------------
  function save( new_name ) {
    //-----------------------------------------------------------------
    const temp = {}
    for( let panel of layout.values() ) {
      temp[panel.id] = panel.info();
    }
    const value = JSON.stringify( temp );
    localStorage.setItem( new_name || name, value );
  }

  //#endregion

  //#region [METHOD] === INTERNAL

  //-----------------------------------------------------------------
  function getParent(id) {
    //-----------------------------------------------------------------
    for( let panel of layout.values() ) {
      if( panel.items.includes(id) ) {
        return panel;
      }
    }
  }

  //-----------------------------------------------------------------
  function createDefaultPanel() {
    //-----------------------------------------------------------------
    const _panel = {
      id        : ref_auto_id.current++ ,
      type      : PanelType.PANEL ,
      size      : 50 ,
      open      : true ,
      direction : PanelDirection.ROW ,
    }
    return new PanelData( _panel );
  }

  //-----------------------------------------------------------------
  function loadLayout(name) {
    //-----------------------------------------------------------------
    try {
      console.info('load layout:', name)
      const _layout_data = localStorage.getItem( name );
      if( _layout_data == null ) return;

      const parsed = JSON.parse( _layout_data );
      const result = new Map();
      for( let item of Object.values(parsed) ) {
        result.set( item.id, new PanelData(item) );
      }

      const max_id = Math.max( ...result.keys() );
      ref_auto_id.current = max_id + 1;

      return result;
    } catch (error) {
      console.warn( {error} );
    }
  }

  //#endregion

  return (
    <ContextLayout.Provider value={ values }>
      {children}
    </ContextLayout.Provider>
  );
};

//-----------------------------------------------------------------
export function useLayout() {
  //-----------------------------------------------------------------
  return useContext( ContextLayout );
}

//-----------------------------------------------------------------
function createDefaultLayout() {
  //-----------------------------------------------------------------
  try {
    const _panel_1 = {
      id        : 1 ,
      type      : PanelType.ROOT ,
      size      : 100 ,
      open      : true ,
      direction : PanelDirection.COL ,
      is_locked : true,
      items     : [2,3] ,
    }
    const _panel_2 = {
      id        : 2 ,
      type      : PanelType.PANEL ,
      size      : 50 ,
      open      : true ,
      direction : PanelDirection.ROW ,
      is_locked : true,
      items     : [4,5] ,
    }
    const _panel_3 = {
      id        : 3 ,
      type      : PanelType.PANEL ,
      size      : 50 ,
      open      : true ,
      direction : PanelDirection.COL ,
      is_locked : true,
      items     : [6,7,8] ,
    }
    const _panel_4 = {
      id        : 4 ,
      type      : PanelType.DOCUMENTS ,
      size      : 50 ,
      open      : true ,
    }
    const _panel_5 = {
      id        : 5 ,
      type      : PanelType.COMMENTS ,
      size      : 50 ,
      open      : true ,
    }
    const _panel_6 = {
      id        : 6 ,
      type      : PanelType.REVIEW ,
      size      : 40 ,
      open      : true ,
    }
    const _panel_7 = {
      id        : 7 ,
      type      : PanelType.THUMB ,
      size      : 20 ,
      open      : true ,
    }
    const _panel_8 = {
      id        : 8 ,
      type      : PanelType.IMAGE ,
      size      : 40 ,
      open      : true ,
    }
    const _default_panel_1 = new PanelData(_panel_1);
    const _default_panel_2 = new PanelData(_panel_2);
    const _default_panel_3 = new PanelData(_panel_3);
    const _default_panel_4 = new PanelData(_panel_4);
    const _default_panel_5 = new PanelData(_panel_5);
    const _default_panel_6 = new PanelData(_panel_6);
    const _default_panel_7 = new PanelData(_panel_7);
    const _default_panel_8 = new PanelData(_panel_8);
    const _default_layout = new Map();
    _default_layout.set( _default_panel_1.id , _default_panel_1 );
    _default_layout.set( _default_panel_2.id , _default_panel_2 );
    _default_layout.set( _default_panel_3.id , _default_panel_3 );
    _default_layout.set( _default_panel_4.id , _default_panel_4 );
    _default_layout.set( _default_panel_5.id , _default_panel_5 );
    _default_layout.set( _default_panel_6.id , _default_panel_6 );
    _default_layout.set( _default_panel_7.id , _default_panel_7 );
    _default_layout.set( _default_panel_8.id , _default_panel_8 );

    return _default_layout;
  } catch (error) {
    console.warn( {error} );
  }
}